import React from "react";
import { ShieldCheck, BadgeCheck, LifeBuoy } from "lucide-react";
import { useLang } from "../../context/LanguageContext";

/**
 * Tre skäl att våga boka av en främling.
 *
 * **Varje påstående här är kontrollerat mot vad koden faktiskt gör**, eftersom
 * en trygghetssektion är den plats där ett obelagt påstående gör mest skada. Tre
 * saker i designunderlaget är strukna och ett är omskrivet:
 *
 *   1. "Pengarna är låsta" och "Er betalning hålls säkert" beskriver ett
 *      escrow-upplägg. Vi håller inga pengar. Det är en destination charge där
 *      artisten är säljare och Stripe skjuter upp utbetalningen fjorton dagar.
 *      Skillnaden är juridisk och inte semantisk: escrow är en reglerad
 *      verksamhet. Texten säger nu vad som verkligen händer.
 *   2. "Verifierad mot Bolagsverket" görs inte. Ingen integration finns.
 *   3. "Minst en tidigare kund" verifieras inte heller.
 *   4. "99,7 % av bokningar sker utan tvist" är ett påhittat tal. Vi har ingen
 *      historik att räkna det på, och den dagen vi har den kan siffran mätas.
 *
 * Kvar står tre saker som är sanna, och de räcker.
 */

const T = {
  sv: {
    eyebrow: "Trygghet",
    heading: "Talangen får betalt efter eventet.",
    cards: [
      { Icon: ShieldCheck, title: "Betalningen hålls till efter eventet",
        body: "Du betalar när du bokar, men talangen får sina pengar först fjorton dagar efter att eventet är genomfört. Betalningen hanteras av Stripe. Blir eventet inställt följer återbetalningen avbokningsvillkoren i avtalet ni båda signerat." },
      { Icon: BadgeCheck, title: "Varje profil är granskad",
        body: "Vi går igenom varje talangprofil manuellt innan den publiceras, och F-skatt kontrolleras för den som fakturerar. En profil som inte är komplett syns inte i katalogen och kan inte ta emot förfrågningar." },
      { Icon: LifeBuoy, title: "Vi går in när det behövs",
        body: "Blir något fel hör du av dig till oss. Vi läser avtalet, meddelandena och betalningen, och medlar mellan er. Vi tar inte automatiskt någons parti, men vi låter det inte heller ligga." },
    ],
  },
  en: {
    eyebrow: "Trust",
    heading: "The talent gets paid after the event.",
    cards: [
      { Icon: ShieldCheck, title: "Payment is held until after the event",
        body: "You pay when you book, but the talent is paid fourteen days after the event has taken place. Payments are handled by Stripe. If the event is cancelled, refunds follow the cancellation terms in the agreement you both signed." },
      { Icon: BadgeCheck, title: "Every profile is reviewed",
        body: "We review each talent profile manually before it is published, and Swedish F-skatt is checked for anyone invoicing. An incomplete profile is not listed and cannot receive requests." },
      { Icon: LifeBuoy, title: "We step in when needed",
        body: "If something goes wrong, you contact us. We read the agreement, the messages and the payment, and mediate between you. We do not automatically take a side, but we do not leave it either." },
    ],
  },
};

const TrustBlock = () => {
  const { lang } = useLang();
  const t = T[lang === "sv" ? "sv" : "en"];
  return (
    <section className="border-t border-[#1e1e1e] bg-[#0F0F0F]" data-testid="trust-block">
      <div className="max-w-[1100px] mx-auto px-6 py-20 md:py-24">
        <div className="text-center mb-14">
          <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-3">{t.eyebrow}</div>
          <h2 className="text-[34px] md:text-[46px] font-extrabold tracking-tight text-white leading-[1.08]">
            {t.heading}
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {t.cards.map(({ Icon, title, body }) => (
            <div key={title} data-testid={`trust-${title.slice(0, 12)}`}
              className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-7 hover:border-[#3A3A3A] transition-colors">
              <div className="w-11 h-11 rounded-xl bg-[#FF5C00]/10 border border-[#FF5C00]/25 flex items-center justify-center mb-5">
                <Icon size={20} className="text-[#FF5C00]" />
              </div>
              <h3 className="text-[17px] font-bold text-white mb-2">{title}</h3>
              <p className="text-[13.5px] text-neutral-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustBlock;
